import { useClientApplicationQuery } from "@graphql/client-applications/ClientApplications.g";
import { RouteObject, UIMatch, redirect } from "react-router";

import { queryClient } from "@api/queries";

import { CurrentClientName } from "@components/Texts";

import { PortalPage } from "@layouts/PortalPage/PortalPage.layout";
import { ExtensionInstallationGuard } from "@components/RouteGuards/ExtensionInstallationGuard/ExtensionInstallationGuard";

import { routes } from "@pages/routes";

import { Application } from "./Application/Application.page";
import { ApplicationDashboard } from "./ApplicationDashboard/ApplicationDashboard.page";
import { ApplicationRecord } from "./ApplicationRecord/ApplicationRecord.page";
import { ApplicationUsers } from "./ApplicationUsers/ApplicationUsers.page";
import { ApplicationNotifications } from "./ApplicationNotifications/ApplicationNotifications.page";
import { ApplicationFileUploads } from "./ApplicationFileUploads/ApplicationFileUploads.page";
import { ApplicationSharedCredentials } from "./ApplicationSharedCredentials/ApplicationSharedCredentials.page";
import { Applications } from "./Applications/Applications.page";

export const applicationRoutes = [
  {
    path: routes.portal.clients.client.applications.index.$path(),
    Component: PortalPage,
    handle: {
      breadcrumb: ({ params }: UIMatch) => ({
        reset: true,
        title: <CurrentClientName />,
        path: routes.portal.clients.client.dashboard.$buildPath({
          params: { clientId: params.clientId as string },
        }),
      }),
    },
    children: [
      {
        Component: ExtensionInstallationGuard,
        handle: {
          breadcrumb: {
            title: "Applications",
          },
        },
        children: [
          {
            index: true,
            Component: Applications,
            handle: { fullBleed: true },
          },
          {
            path: routes.portal.clients.client.applications.application.$path(),
            Component: Application,
            handle: {
              breadcrumb: async ({
                params: { applicationId = "", clientId = "" },
              }: UIMatch) => {
                // applicationId is the client application ID
                const data = await queryClient.fetchQuery({
                  queryKey: useClientApplicationQuery.getKey({
                    clientId,
                    clientApplicationId: applicationId,
                  }),
                  queryFn: useClientApplicationQuery.fetcher({
                    clientId,
                    clientApplicationId: applicationId,
                  }),
                });

                // Get the resource product name and logo
                const productName = data?.clientApplication?.resource?.productName;

                return {
                  title: productName,
                };
              },
            },
            children: [
              {
                index: true,
                loader: ({ params: { applicationId = "", clientId = "" } }) => {
                  return redirect(
                    routes.portal.clients.client.applications.application.dashboard.$buildPath(
                      {
                        params: { clientId, applicationId },
                      },
                    ),
                  );
                },
              },
              {
                path: routes.portal.clients.client.applications.application.dashboard.$path(),
                Component: ApplicationDashboard,
                handle: {
                  breadcrumb: {
                    title: "Dashboard",
                  },
                },
              },
              {
                path: routes.portal.clients.client.applications.application.record.$path(),
                Component: ApplicationRecord,
                handle: {
                  breadcrumb: {
                    title: "Record",
                  },
                },
              },
              {
                path: routes.portal.clients.client.applications.application.users.$path(),
                Component: ApplicationUsers,
                handle: {
                  fullBleed: true,
                  breadcrumb: {
                    title: "Users",
                  },
                },
              },
              {
                path: routes.portal.clients.client.applications.application.notifications.$path(),
                Component: ApplicationNotifications,
                handle: {
                  fullBleed: true,
                  breadcrumb: {
                    title: "Notifications",
                  },
                },
              },
              {
                path: routes.portal.clients.client.applications.application.fileUploads.$path(),
                Component: ApplicationFileUploads,
                handle: {
                  fullBleed: true,
                  breadcrumb: {
                    title: "File Uploads",
                  },
                },
              },
              {
                path: routes.portal.clients.client.applications.application.sharedCredentials.$path(),
                Component: ApplicationSharedCredentials,
                handle: {
                  fullBleed: true,
                  breadcrumb: {
                    title: "Shared Credentials",
                  },
                },
              },
            ],
          },
        ],
      },
    ],
  },
] satisfies RouteObject[];
